/* eslint-disable no-underscore-dangle */
import { Logger } from 'vue-infra';
import { each, isUndefined, isNull, includes } from 'lodash';
import { ROOT_GETTER_PATHS } from '@core/store';
import { DATA_LOADING_STATES,
  METRIC_NAMES, RETRY_INTERVAL,
  WIDGET_LOGICAL_NAMES } from '@config/Consts';
import { isMaxNumRetriesReached, getInterceptor, isEventRelevant } from '@core/utils/utils';
import ShiftStatusService from '@core/services/shift-status/ShiftStatusService';
import AgentStatusMetricsModel from '@core/models/AgentStatusMetricsModel';
import ManagerMetricsModel from '@core/models/ManagerMetricsModel';
import CSATModel from '@core/models/CSATModel';
import MsgConversationMetricsModel from '@core/models/MsgConversationMetricsModel';
import { RESOURCES as REPORTING_RESOURCES } from '@core/clients/ReportingClient';
import { AGENT_STATUS_RESOURCES } from '@core/clients/MsgHistoryClient';
import { RESOURCES as AGENT_MANAGER_RESOURCES } from '@core/clients/AgentManagerClient';
import { Subscription } from '@core/common/Subscription';
import QueueHealthModel from '@core/models/QueueHealthModel';

const logger = Logger.getLogger('shiftStatus');

const RESOURCES_METRICS_MAP = {
  [REPORTING_RESOURCES.MESSAGING_CONVERSATIONS]: [
    METRIC_NAMES.CLOSED_CONVERSATIONS,
    // METRIC_NAMES.RESPONSE_TIME,// Remove response time
  ],
  [AGENT_STATUS_RESOURCES.AGENT_STATUS_LOAD]: [
    METRIC_NAMES.LOAD_CONVERSATIONS,
  ],
  [AGENT_STATUS_RESOURCES.AGENT_STATUS_ASSIGNED]: [
    METRIC_NAMES.ASSIGNED_CONVERSATIONS,
  ],
  [REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS]: [
    METRIC_NAMES.CSAT,
  ],
  [AGENT_MANAGER_RESOURCES.METRICS]: [
    METRIC_NAMES.CLOSED_CONVERSATIONS,
    METRIC_NAMES.ASSIGNED_CONVERSATIONS,
    METRIC_NAMES.AVG_WAIT_TIME,
    METRIC_NAMES.AVG_WAIT_TIME_FIRST_RESPONSE,
    METRIC_NAMES.AVG_TIME_TO_RESPONSE,
    METRIC_NAMES.AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT,
    METRIC_NAMES.HUMAN_ONLINE_LOAD,
    METRIC_NAMES.HUMAN_AWAY_LOAD,
    METRIC_NAMES.LOAD_CONVERSATIONS,
    METRIC_NAMES.CLOSED_BY_CONSUMER,
    METRIC_NAMES.CLOSED_BY_AGENT,
    METRIC_NAMES.AUTO_CLOSED,
    METRIC_NAMES.AVG_CONVERSATIONS_DURATION,
  ],
  [REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH]: [
    METRIC_NAMES.OVERDUE_ASSIGNED,
    METRIC_NAMES.OVERDUE_IN_QUEUE,
    METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS,
  ],
};

const RESOURCES_MODEL_MAP = {
  [REPORTING_RESOURCES.MESSAGING_CONVERSATIONS]: MsgConversationMetricsModel,
  [AGENT_STATUS_RESOURCES.AGENT_STATUS_ASSIGNED]: AgentStatusMetricsModel,
  [AGENT_STATUS_RESOURCES.AGENT_STATUS_LOAD]: AgentStatusMetricsModel,
  [REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS]: CSATModel,
  [AGENT_MANAGER_RESOURCES.METRICS]: ManagerMetricsModel,
  [REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH]: QueueHealthModel,
};

const ALLOWED_METRICS_NOT_RETURNING_DATA = [
  METRIC_NAMES.AVGֹֹ_WAIT_TIME,
  METRIC_NAMES.AVG_WAIT_TIME_FIRST_RESPONSE,
  METRIC_NAMES.AVG_TIME_TO_RESPONSE,
  METRIC_NAMES.AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT,
  METRIC_NAMES.LOAD_CONVERSATIONS,
  METRIC_NAMES.CSAT,
  METRIC_NAMES.AVG_WAIT_TIME,
  METRIC_NAMES.HUMAN_ONLINE_LOAD,
  METRIC_NAMES.HUMAN_AWAY_LOAD,
  METRIC_NAMES.AVG_CONVERSATIONS_DURATION,
];

const _createSubscriptionPayload = (getters) => {
  logger.debug('Shift Status::actions::_createSubscriptionPayload');
  return {
    timeframe: getters.getTimeFrameFilter,
    agentIds: getters.getUserIds,
    humanAgentIds: getters.getHumanUserIds,
    groupIds: getters.getFilteredGroupsIds,
    skills: getters.getSkillsFilter,
    useNewManagerApi: getters.getUseNewManagerApi,
    fetchResponseMetrics: getters.getFetchResponseMetricsState,
  };
};

const _handleMetricsDataLoading = (commit, resourceName, loadingStatus) => {
  logger.debug('Shift Status::actions::_handleMetricsDataLoading');
  each(RESOURCES_METRICS_MAP[resourceName], (metricName) => {
    commit('setMetricDataLoading', { metricName, loadingStatus });
  });
};

const _handleFirstTimeLoadNotReturnedValue = (commit, metricName) => {
  // for these metrics if they are not returned on first load we set a value of 0
  const firstTimeNotReturningValueMetrics = [
    METRIC_NAMES.CLOSED_CONVERSATIONS,
    METRIC_NAMES.ASSIGNED_CONVERSATIONS,
    METRIC_NAMES.OVERDUE_ASSIGNED,
  ];
  if (firstTimeNotReturningValueMetrics.includes(metricName)) {
    commit('setMetric', { metricName, value: 0 });
  }
};

const _handleSubscriptionSuccess = ({ commit, getters }, resourceName, response) => {
  logger.debug('Shift Status::actions::_handleSubscriptionSuccess');
  if (response && response.data && response.data instanceof RESOURCES_MODEL_MAP[resourceName]) {
    each(RESOURCES_METRICS_MAP[resourceName], (metricName) => {
      const metricValue = response.data[metricName];
      // some metrics which don't return data are not considered as a wrong format.
      // there is no registered data and the api just does not include them in the response.
      // example then TTR metrics, the load metric.
      // In this case they will be assigned with the '--' value.
      // there are also metrics that always return the same default value (like 0 for csat) even if
      // there are no csat answers. In this specific case they will be assigned with the '--' value.
      // these specific metrics are listed in ALLOWED_METRICS_NOT_RETURNING_DATA.
      // https://docs.google.com/spreadsheets/d/1HY4Pf6pKTsO341-SwW10rfMTNRSfRY44gVa3fdzAc50/edit#gid=1063822623
      if (isUndefined(metricValue) && !includes(ALLOWED_METRICS_NOT_RETURNING_DATA, metricName)) {
        commit('setMetricDataLoading', { metricName, loadingStatus: DATA_LOADING_STATES.WRONG_FORMAT });
        const currentMetricValue = getters.getMetricData(metricName);
        if (isNull(currentMetricValue)) {
          _handleFirstTimeLoadNotReturnedValue(commit, metricName);
        }
      } else {
        commit('setMetric', { metricName, value: metricValue });
        commit('setMetricDataLoading', { metricName, loadingStatus: DATA_LOADING_STATES.SUCCESS });
      }
    });
  } else {
    _handleMetricsDataLoading(commit, resourceName, DATA_LOADING_STATES.WRONG_FORMAT);
  }
};

const _onSubscriptionUpdateNotification = (commit, getters, resourceName, response) => {
  logger.debug('Shift Status::actions::_onSubscriptionUpdateNotification', { resourceName, response });
  _handleSubscriptionSuccess({ commit, getters }, resourceName, response);
  if (getters.getFirstTimeLoading) {
    commit('setFirstTimeLoading', false);
  }
};

const _onSubscriptionError = ({ commit, getters, rootGetters }, resourceName, err) => {
  if (err && !err.cancelled) {
    logger.error('Shift Status::actions::_onSubscriptionError', JSON.stringify({ resourceName, err }));
    _handleMetricsDataLoading(commit, resourceName, DATA_LOADING_STATES.ERROR);
    if (isMaxNumRetriesReached(err)) {
      // eslint-disable-next-line no-use-before-define
      setTimeout(() => updateSubscription({ getters, rootGetters }), RETRY_INTERVAL);
    }
  }
};

const _handleSubscriptions = async ({ commit, getters, rootGetters }, subscriptions) => {
  if (subscriptions && subscriptions.subscriptions) {
    each(subscriptions.subscriptions, (subscription) => {
      subscription.subscription.clearAllEventListeners();
      subscription.subscription.on(Subscription.EVENTS.onSubscriptionUpdateNotification,
        _onSubscriptionUpdateNotification.bind(this, commit, getters, subscription.name),
        'activitySummarySubscriptionUpdateNotificationSuccess');
      subscription.subscription.on(Subscription.EVENTS.onSubscriptionError,
        _onSubscriptionError.bind(this, { commit, getters, rootGetters }, subscription.name),
        'activitySummarySubscriptionUpdateNotificationError');
      const subscriptionPayload = _createSubscriptionPayload(getters);
      _handleMetricsDataLoading(commit, subscription.name, DATA_LOADING_STATES.LOADING);
      try {
        logger.debug('Shift Status::actions::before executing start subscription');
        subscription.subscription.start({ settings: subscriptionPayload, name: subscription.name });
      } catch (ex) {
        logger.error('cannot start SS subscription', 'start', { ex, subscription });
      }
    });
  }
};

const startSubscription = async ({ commit, getters, rootGetters }) => {
  logger.debug('Shift Status::actions::startSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.SHIFT_STATUS)) {
    logger.debug('Shift Status::no permission to start the shift status widget subscription');
    return;
  }
  const subscriptions = await
  ShiftStatusService.getShiftStatusSubscriptions({
    useNewManagerApi: getters.getUseNewManagerApi,
    fetchResponseMetrics: getters.getFetchResponseMetricsState,
    interceptor: getInterceptor.bind(this, getters),
  });
  await _handleSubscriptions({ commit, getters, rootGetters }, subscriptions);
};

const stopSubscription = async ({ commit, getters, rootGetters }) => {
  logger.debug('Shift Status::actions::stopSubscription', { commit, getters });
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.SHIFT_STATUS)) {
    logger.debug('Shift Status::no permission to stop the shift status widget subscription');
    return;
  }
  const subscriptions = await
  ShiftStatusService.getShiftStatusSubscriptions({
    useNewManagerApi: getters.getUseNewManagerApi,
    fetchResponseMetrics: getters.getFetchResponseMetricsState,
    interceptor: getInterceptor.bind(this, getters),
  });
  if (subscriptions && subscriptions.subscriptions) {
    each(subscriptions.subscriptions, (subscription) => {
      subscription.subscription.clearAllEventListeners();
      try {
        logger.debug('Shift Status::actions::before executing stop subscription');
        subscription.subscription.stop({ name: subscription.name });
        _handleMetricsDataLoading(commit, subscription.name, DATA_LOADING_STATES.NONE);
      } catch (ex) {
        logger.error('cannot stop SS subscription', 'stop', { ex, subscription });
      }
    });
  }
};

const updateSubscription = async ({ getters, rootGetters }) => {
  logger.debug('Shift Status::actions::updateSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.SHIFT_STATUS)) {
    logger.debug('Shift Status::no permission to update the shift status widget subscription');
    return;
  }
  const subscriptions = await
  ShiftStatusService.getShiftStatusSubscriptions({
    useNewManagerApi: getters.getUseNewManagerApi,
    fetchResponseMetrics: getters.getFetchResponseMetricsState,
    interceptor: getInterceptor.bind(this, getters),
  });
  if (subscriptions && subscriptions.subscriptions) {
    each(subscriptions.subscriptions, async (subscription) => {
      const subscriptionPayload = _createSubscriptionPayload(getters);
      try {
        const updateData = { settings: subscriptionPayload, name: subscription.name };
        await subscription.subscription.update(updateData);
      } catch (ex) {
        logger.error('cannot update Shift Status subscription', 'update', { ex, subscription });
      }
    });
  }
};

const handleStartSubscription = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.SHIFT_STATUS, widgets)) {
    startSubscription({ commit, getters, rootGetters });
  }
};
const handleStopSubscription = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.SHIFT_STATUS, widgets)) {
    stopSubscription({ commit, getters, rootGetters });
  }
};
const handleUpdateSubscription = ({ getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.SHIFT_STATUS, widgets)) {
    updateSubscription({ getters, rootGetters });
  }
};
const handleShowWidgets = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.SHIFT_STATUS, widgets)) {
    startSubscription({ commit, getters, rootGetters });
  } else {
    stopSubscription({ commit, getters, rootGetters });
  }
};

const addAlertToHistory = ({ dispatch }, alertModel) => {
  dispatch('amd/alertsHistory/addAlerts', alertModel, { root: true });
};

export default {
  RESOURCES_METRICS_MAP, // In use by Unit tests
  handleStartSubscription,
  handleStopSubscription,
  handleUpdateSubscription,
  handleShowWidgets,
  addAlertToHistory,
};
